import { css } from '@emotion/react'
import {
  CornerRadius,
  LineWeight,
  Spacing,
  ThemedColors,
  ThemedFocusIndicator,
  Typography,
  gemVar,
} from '@opallabs/gem'

const rootClass = 'opal-calendar-toolbar'
export const selectors = {
  viewMenu: `${rootClass}-view-menu`,
} as const

/** The inset every block of the sheet keeps from its edges. */
const inset = Spacing.M

/** How far a section's heading stands from its rows: Include's is the wider. */
export const SECTION_HEADING_GAP = Spacing.XS * 3
export const INCLUDE_HEADING_GAP = Spacing.M

/** How far apart the rows of a section stand, section by section. */
export const OBJECT_ROW_GAP = Spacing.S
export const TITLE_ROW_GAP = Spacing.XS * 3
export const LABEL_ROW_GAP = Spacing.S
export const DETAIL_ROW_GAP = Spacing.XS

/**
 * The sheet, from the Figma "Create Popover" section (4421:33034 and
 * 4421:33150): 300px wide, its blocks each inset 16px and parted by rules that
 * run the whole width.
 */
/**
 * The popover round the sheet. Lapidary's popover rounds its own box, the
 * child that carries the surface and the shadow, at 4px; the design's settings
 * popover is rounded at 8px.
 */
export const viewMenuPopover = css({
  '& > div': { borderRadius: CornerRadius.Large },
})

export const viewMenu = css({
  // The sheet's own ink. The month view is mounted into a page whose body
  // text is grey, and a mark that inherits it goes grey with it.
  color: gemVar(ThemedColors.TextPrimary),
  display: 'flex',
  flexDirection: 'column',
  inlineSize: Spacing.XS * 75,
})

/** A block of the sheet with no heading: the rows under a pane's title, or the grid row. */
export const viewMenuBlock = css({
  display: 'flex',
  flexDirection: 'column',
  gap: Spacing.M,
  padding: inset,

  // The block under a pane's title is spaced by the title's own row.
  'button + &': {
    paddingBlockStart: 0,
  },
})

/** A headed block: Include, Titles, Labels, Other Details. */
export const viewMenuSection = (headingGap: number) =>
  css({
    display: 'flex',
    flexDirection: 'column',
    gap: headingGap,
    padding: inset,
  })

export const viewMenuRows = (gap: number) =>
  css({
    display: 'flex',
    flexDirection: 'column',
    gap,
  })

/** A section's name, and the title of a pane. */
export const viewMenuTitle = css({
  ...Typography.H50,
  color: gemVar(ThemedColors.TextPrimary),
  margin: 0,
})

export const viewMenuTitleGroup = css({
  display: 'flex',
  flexDirection: 'column',
  gap: Spacing.XS,
})

/** A row of the sheet: a label on the left, whatever it sets on the right. */
export const viewMenuRow = css({
  alignItems: 'center',
  display: 'flex',
  gap: Spacing.S,
  justifyContent: 'space-between',
  minBlockSize: Spacing.L,
})

export const viewMenuRowLabel = css({
  ...Typography.T500,
  color: gemVar(ThemedColors.TextPrimary),
  marginInlineEnd: 'auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

/**
 * The design's "select button": a select with the box taken off, 24px tall,
 * so the row reads as a label and its answer. The box comes back only as the
 * menu's own surface.
 */
export const viewMenuSelect = css({
  display: 'inline-flex',
  flex: 'none',

  '.lapidary-single-select-trigger': {
    ...Typography.T500,
    backgroundColor: 'transparent',
    blockSize: Spacing.L,
    border: 'none',
    color: gemVar(ThemedColors.TextPrimary),
    gap: Spacing.XS,
    paddingBlock: 0,
    paddingInline: `${Spacing.S}px ${Spacing.XS}px`,
  },
})

/**
 * A kind's row: its switch, its mark, its name, and the way into its pane. The
 * part after the switch is a menu item in the design, inset 8px at both ends.
 */
export const viewMenuObjectRow = css({
  alignItems: 'center',
  // The kind's mark is drawn in the standard icon ink, whatever the row's
  // text is set in.
  '.lapidary-icon': { color: gemVar(ThemedColors.IconPrimary) },
  display: 'flex',
  gap: Spacing.XS,
  minBlockSize: Spacing.XS * 8,
  paddingInlineEnd: Spacing.S,

  '> :first-child': {
    marginInlineEnd: Spacing.XS,
  },
})

/**
 * Holds a Lapidary switch at the size Lapidary draws it. A switch is a flex
 * item like any other, and without this a long row label would squeeze it.
 */
export const viewMenuSwitch = css({
  alignItems: 'center',
  display: 'flex',
  flex: 'none',
  // The design's frame round a switch: 4px beside it and 8px above and
  // below, which is what makes a switch row 32px tall.
  padding: `${Spacing.S}px ${Spacing.XS}px`,
})

/**
 * The row that leaves a pane. It is the whole width, so it is easy to hit, and
 * it carries the pane's name: inside a pane this row is the sheet's title.
 */
export const viewMenuBackRow = css({
  alignItems: 'center',
  background: 'none',
  border: 'none',
  color: gemVar(ThemedColors.TextPrimary),
  cursor: 'pointer',
  display: 'flex',
  gap: Spacing.XS,
  inlineSize: '100%',
  padding: `${inset}px ${inset}px ${Spacing.S}px ${Spacing.S}px`,
  textAlign: 'start',
})

/** The chevron's 24px square, so it sits where the design's back button does. */
export const viewMenuBackIcon = css({
  alignItems: 'center',
  display: 'inline-flex',
  justifyContent: 'center',
  padding: Spacing.XS,
})

/** A checkbox and its label, drawn as a menu item: 32px tall, inset 8px. */
export const viewMenuCheckRow = css({
  alignItems: 'center',
  display: 'flex',
  gap: Spacing.S,
  minBlockSize: Spacing.XS * 8,
  paddingInline: Spacing.S,
})

export const viewMenuCheckLabel = css({
  ...Typography.T500,
  color: gemVar(ThemedColors.TextPrimary),
  cursor: 'pointer',
})

export const viewMenuNote = css({
  ...Typography.T300,
  color: gemVar(ThemedColors.TextSecondary),
  margin: 0,
})

export const viewMenuDivider = css({
  backgroundColor: gemVar(ThemedColors.StrokeSecondary1),
  blockSize: 1,
  border: 'none',
  margin: 0,
})

/**
 * The sort control's select, drawn as a toolbar button: a 32px ghost face
 * beside the Filter button, taking the same tint and hairline on hover and
 * while its menu is open, with no chevron of its own.
 */
export const sortControl = css({
  display: 'inline-flex',

  '.lapidary-single-select-trigger': {
    ...Typography.T500,
    backgroundColor: 'transparent',
    blockSize: Spacing.XS * 8,
    border: `${LineWeight.Small}px solid transparent`,
    borderRadius: CornerRadius.Small,
    color: gemVar(ThemedColors.TextPrimary),
    gap: Spacing.XS + 1,
    paddingBlock: 0,
    paddingInline: Spacing.S * 1.25,

    '&:hover, &[aria-expanded="true"]': {
      backgroundColor: gemVar(ThemedColors.Primary2),
      borderColor: gemVar(ThemedColors.StrokePrimary1),
    },
    '&[aria-expanded="true"]': {
      backgroundColor: gemVar(ThemedColors.Primary3),
    },
    '&:focus-visible': ThemedFocusIndicator,
  },
  '.single-select-toggle': { display: 'none' },
})
